import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { LocalStore } from "../../storage/authStore";
import { TestimonialServices } from "./TestimonialServices";

const emptyForm = {
  name: "",
  location: "",
  review: "",
  rating: 5,
  image: "",
  sortOrder: 0,
  isActive: true,
};

const normalizeRoleTitle = (title = "") => title.toString().trim().toLowerCase().replace(/[\s_-]+/g, "");

export default function Testimonials() {
  const dispatch = useDispatch();
  const loggedUser = LocalStore.getLoggedUserDetails();
  const isSuperAdmin = normalizeRoleTitle(loggedUser?.role?.title) === "superadmin";

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });

  const formTitle = useMemo(() => editingId ? "Edit Testimonial" : "Add Testimonial", [editingId]);

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "testimonials" });
  }, [dispatch]);

  const fetchList = useCallback(async (nextSearchKey = searchKey) => {
    try {
      setLoading(true);
      const response = await TestimonialServices.list({
        page: pagination.page,
        limit: pagination.pageSize,
        searchKey: nextSearchKey,
      });

      if (Number(response.success) === 1) {
        setRows(response.data.list || []);
        setPagination((current) => ({ ...current, total: response.data.totalItems || 0 }));
      }
    } catch (error) {
      Swal.fire(error.message, "", "error");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchKey]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchList();
    }
  }, [fetchList, isSuperAdmin]);

  if (!isSuperAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.location.trim() || !form.review.trim()) {
      Swal.fire("Name, location and review are required", "", "warning");
      return;
    }

    const rating = Number(form.rating);
    if (Number.isNaN(rating) || rating < 0 || rating > 5) {
      Swal.fire("Rating must be between 0 and 5", "", "warning");
      return;
    }

    const payload = {
      ...form,
      rating,
      sortOrder: Number(form.sortOrder) || 0,
    };

    const response = editingId
      ? await TestimonialServices.update(editingId, payload)
      : await TestimonialServices.create(payload);

    if (Number(response.success) === 1) {
      Swal.fire(response.message || "Saved successfully", "", "success");
      resetForm();
      fetchList();
    }
  };

  const handleEdit = (row) => {
    setEditingId(row._id);
    setForm({
      name: row.name || "",
      location: row.location || "",
      review: row.review || "",
      rating: row.rating ?? 5,
      image: row.image || "",
      sortOrder: row.sortOrder || 0,
      isActive: Boolean(row.isActive),
    });
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Delete testimonial?",
      text: `${row.name}'s testimonial will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) {
      return;
    }

    const response = await TestimonialServices.remove(row._id);
    if (Number(response.success) === 1) {
      Swal.fire(response.message || "Deleted successfully", "", "success");
      fetchList();
    }
  };

  const handleSearch = () => {
    setPagination((current) => ({ ...current, page: 0 }));
    fetchList(searchKey);
  };

  return (
    <>
      <PageHeaderLayout headerTitle="Manage Testimonials">
        <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={resetForm}>
          Reset
        </Button>
      </PageHeaderLayout>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{formTitle}</Typography>
            <Stack component="form" spacing={2} onSubmit={handleSubmit}>
              <TextField label="Name" value={form.name} onChange={(e) => updateField("name", e.target.value)} required size="small" />
              <TextField label="Location" value={form.location} onChange={(e) => updateField("location", e.target.value)} required size="small" />
              <TextField label="Review" value={form.review} onChange={(e) => updateField("review", e.target.value)} required multiline minRows={5} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField label="Rating" type="number" inputProps={{ min: 0, max: 5, step: 0.5 }} value={form.rating} onChange={(e) => updateField("rating", e.target.value)} required size="small" fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Sort Order" type="number" value={form.sortOrder} onChange={(e) => updateField("sortOrder", e.target.value)} size="small" fullWidth />
                </Grid>
              </Grid>
              <TextField label="Image URL" value={form.image} onChange={(e) => updateField("image", e.target.value)} size="small" />
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => updateField("isActive", e.target.checked)} />}
                label="Active on website"
              />
              <Button type="submit" variant="contained" startIcon={editingId ? <SaveIcon /> : <AddCircleIcon />}>
                {editingId ? "Update" : "Add"}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
              <TextField
                label="Search testimonials"
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                size="small"
                fullWidth
              />
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
                Search
              </Button>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 380 }}>
                          {row.review}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell>{row.rating}</TableCell>
                      <TableCell>
                        <Chip size="small" color={row.isActive ? "success" : "default"} label={row.isActive ? "Active" : "Inactive"} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>No testimonials found</Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page}
              rowsPerPage={pagination.pageSize}
              onPageChange={(event, page) => setPagination((current) => ({ ...current, page }))}
              onRowsPerPageChange={(event) => setPagination({ page: 0, pageSize: parseInt(event.target.value, 10), total: pagination.total })}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
